import { authenticatedRequest } from './authApi';
import { logOperationalWarning } from '../utils/safeLogger';
import { getAuthToken } from '../utils/auth';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const forumError = (es, en) => localizedValue({ es, en });
const API_BASE = REST_API_ORIGIN;

function normalizeSign(s) {
  if (!s) return '';
  return s
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

async function handleJson(res) {
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error(data?.message || data?.error || res.statusText || 'Error');
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}



const FORUM_CATEGORY_FALLBACK = [
  { key: 'capricornio', name: 'Capricornio', emoji: '♑' },
  { key: 'acuario', name: 'Acuario', emoji: '♒' },
  { key: 'piscis', name: 'Piscis', emoji: '♓' },
  { key: 'aries', name: 'Aries', emoji: '♈' },
  { key: 'tauro', name: 'Tauro', emoji: '♉' },
  { key: 'geminis', name: 'Géminis', emoji: '♊' },
  { key: 'cancer', name: 'Cáncer', emoji: '♋' },
  { key: 'leo', name: 'Leo', emoji: '♌' },
  { key: 'virgo', name: 'Virgo', emoji: '♍' },
  { key: 'libra', name: 'Libra', emoji: '♎' },
  { key: 'escorpio', name: 'Escorpio', emoji: '♏' },
  { key: 'sagitario', name: 'Sagitario', emoji: '♐' },
];

let forumCategoriesPromise = null;
let forumCategoriesCache = null;
let forumCategoriesCacheTs = 0;
const FORUM_CATEGORIES_TTL_MS = 5 * 60 * 1000;

export async function listCategories({ force = false } = {}) {
  const now = Date.now();

  if (!force && forumCategoriesCache && (now - forumCategoriesCacheTs) < FORUM_CATEGORIES_TTL_MS) {
    return forumCategoriesCache;
  }

  if (!force && forumCategoriesPromise) {
    return forumCategoriesPromise;
  }

  forumCategoriesPromise = (async () => {
    try {
      const r = await authenticatedRequest(`${API_BASE}/api/forum/categories`, {
        rawResponse: true,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        cache: 'no-store',
      });

      if (r.status === 429) {
        forumCategoriesCache = FORUM_CATEGORY_FALLBACK;
        forumCategoriesCacheTs = Date.now();
        return FORUM_CATEGORY_FALLBACK;
      }

      const data = await handleJson(r);
      forumCategoriesCache = Array.isArray(data) && data.length ? data : FORUM_CATEGORY_FALLBACK;
      forumCategoriesCacheTs = Date.now();
      return forumCategoriesCache;
    } catch {
      if (forumCategoriesCache) return forumCategoriesCache;
      return FORUM_CATEGORY_FALLBACK;
    } finally {
      forumCategoriesPromise = null;
    }
  })();

  return forumCategoriesPromise;
}

export async function listThreads(categoryKey, { page = 1, limit = 20 } = {}) {
  const key = normalizeSign(categoryKey);
  const url = new URL(`${API_BASE}/api/forum/${encodeURIComponent(key)}/threads`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));
  const r = await authenticatedRequest(url.toString(), {
    rawResponse: true,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return handleJson(r);
}

export async function getThread(slugOrId) {
  const r = await authenticatedRequest(`${API_BASE}/api/forum/thread/${encodeURIComponent(slugOrId)}`, {
    rawResponse: true,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return handleJson(r);
}

export async function getThreadPosts(threadId, { page = 1, limit = 50 } = {}) {
  const url = new URL(`${API_BASE}/api/forum/thread/${encodeURIComponent(threadId)}/posts`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));
  // evita 304 con payload viejo (sin likedByMe/likesCount)
  // url.searchParams.set('_v', Date.now().toString());

  const r = await authenticatedRequest(url.toString(), {
    rawResponse: true,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });
  return handleJson(r);
}

export async function createThread(categoryKey, { title, body, tags = [] }) {
  const token = getAuthToken();
  if (!token) {
    const err = new Error(forumError('No estás autenticado. Iniciá sesión.', 'You are not authenticated. Please log in.'));
    err.status = 401;
    throw err;
  }
  const key = normalizeSign(categoryKey);
  const r = await authenticatedRequest(`${API_BASE}/api/forum/${encodeURIComponent(key)}/threads`, {
    rawResponse: true,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title, body, tags }),
  });
  try {
    return await handleJson(r);
  } catch (e) {
    logOperationalWarning('forum.createThread', { status: e.status, code: e.status ? `http_${e.status}` : 'request_failed' });
    if (e.status === 401) {
      const msg = (e.payload?.message || '').toLowerCase();
      if (msg.includes('verify') || msg.includes('verific') || msg.includes('email')) {
        e.message = forumError('Tu email no está verificado. Verificalo para poder publicar.', 'Your email is not verified. Verify it before posting.');
      } else {
        e.message = forumError('Sesión inválida o expirada. Iniciá sesión nuevamente.', 'Your session is invalid or expired. Please log in again.');
      }
    } else if (e.status === 403) {
      e.message = forumError('No tenés permisos para crear hilos (¿email sin verificar?).', 'You do not have permission to create threads. Check whether your email is verified.');
    } else if (e.status === 404) {
      e.message = forumError('La categoría de signo no existe.', 'That sign category does not exist.');
    }
    throw e;
  }
}

export async function replyThread(threadId, { body, parentId = null }) {
  const token = getAuthToken();
  if (!token) {
    const err = new Error(forumError('No estás autenticado. Iniciá sesión.', 'You are not authenticated. Please log in.'));
    err.status = 401;
    throw err;
  }
  const r = await authenticatedRequest(`${API_BASE}/api/forum/thread/${encodeURIComponent(threadId)}/posts`, {
    rawResponse: true,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ body, parentId }),
  });
  try {
    return await handleJson(r);
  } catch (e) {
    logOperationalWarning('forum.replyThread', { status: e.status, code: e.status ? `http_${e.status}` : 'request_failed' });
    if (e.status === 401) {
      e.message = forumError('Sesión inválida o expirada. Iniciá sesión nuevamente.', 'Your session is invalid or expired. Please log in again.');
    } else if (e.status === 403) {
      e.message = forumError('No tenés permisos para responder (¿email sin verificar?).', 'You do not have permission to reply. Check whether your email is verified.');
    } else if (e.status === 404) {
      e.message = 'Hilo no encontrado.';
    }
    throw e;
  }
}

// --- Likes en respuestas ---
export async function toggleReplyLike(replyId) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const r = await authenticatedRequest(`${API_BASE}/api/forum/replies/${encodeURIComponent(replyId)}/like`, {
      rawResponse: true,
      method: 'POST',
      headers,
      credentials: 'include',
      cache: 'no-store'
    });
    if (r.status === 401) return { ok: false, error: 'auth_required' };
    return await handleJson(r);
  } catch (e) {
    return { ok: false, error: e?.message || 'network_error' };
  }
}

export async function getReplyLikes(replyId) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const r = await authenticatedRequest(`${API_BASE}/api/forum/replies/${encodeURIComponent(replyId)}/likes`, {
      rawResponse: true,
      method: 'GET',
      headers,
      credentials: 'include'
    });
    if (r.status === 401) return { ok: false, error: 'auth_required' };
    return await handleJson(r);
  } catch (e) {
    return { ok: false, error: e?.message || 'network_error' };
  }
}

// ===== Foro: Notificaciones persistentes =====
export async function getForumNotifications({ unread = false, limit = 20, archived = false } = {}) {
  const url = new URL(`${API_BASE}/api/forum/notifications`);
  if (unread) url.searchParams.set('unread', '1');
  if (archived) url.searchParams.set('archived', '1');
  url.searchParams.set('limit', String(limit));

  const r = await authenticatedRequest(url.toString(), {
    rawResponse: true,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });
  return handleJson(r);
}

export async function markForumNotificationsRead({ all = true, ids = [] } = {}) {
  const r = await authenticatedRequest(`${API_BASE}/api/forum/notifications/mark-read`, {
    rawResponse: true,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ all: !!all, ids: Array.isArray(ids) ? ids : [] }),
  });
  return handleJson(r);
}

// Archivar una notificación (desaparece de la pizarra)
export async function archiveForumNotification(notificationId) {
  const id = typeof notificationId === 'string' ? notificationId : (notificationId?.id || notificationId?._id || '');
  const r = await authenticatedRequest(
    `${API_BASE}/api/forum/notifications/${encodeURIComponent(id)}/archive`,
    {
      rawResponse: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );
  return handleJson(r);
}

// Archivar todas las notificaciones leídas
export async function archiveReadForumNotifications() {
  const r = await authenticatedRequest(
    `${API_BASE}/api/forum/notifications/archive-read`,
    {
      rawResponse: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );
  return handleJson(r);
}
